import { useEffect, useState } from "react";
import {
  useInspectionTopicRewrite,
  type InspectionTopicRewriteOutcome,
  type InspectionTopicRewriteTemplateFailure,
  type InspectionTopicRowInput,
} from "@/hooks/useInspectionTopicRewrite";
import {
  useMyInitialisations,
  type InitialisationProgress,
} from "@/hooks/useMyInitialisations";
import type { EditableTopicRow } from "@/lib/inspectionTopicRows";

export type InspectionTopicRewriteStep = "scope" | "confirmBase" | "progress";

/** Falls back to the raw status for anything not in this table, rather than dropping it. */
const OUTCOME_STATUS_LABELS: Record<string, string> = {
  updated: "Päivitetty",
  skipped_started: "Ohitettu",
  skipped_relinked: "Ohitettu",
  failed: "Epäonnistui",
};

export function outcomeStatusLabel(status: string): string {
  return OUTCOME_STATUS_LABELS[status] ?? status;
}

/**
 * Owns the scope / confirmBase / progress steps that follow a tarkastuskohdat
 * edit: choosing "vain tämä tarkastus" vs. hanke-wide, the Korjaa myös
 * projektipohja confirmation, and — for a hanke-wide run — the background
 * job's progress. Shared by the standalone tarkastuskohdat editor and the
 * tarkastuskohdat section embedded in Muokkaa tehtävän tietoja; the row list
 * and its own "did the user open this flow" state live with the caller.
 */
export function useInspectionTopicRewriteFlow({ open }: { open: boolean }) {
  const { loading: submitting, rewriteTopics } = useInspectionTopicRewrite();

  const [step, setStep] = useState<InspectionTopicRewriteStep>("scope");
  const [applyToWholeProject, setApplyToWholeProject] = useState(false);
  const [applyToBaseTemplate, setApplyToBaseTemplate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [operationId, setOperationId] = useState<string | null>(null);
  const [outcomes, setOutcomes] = useState<InspectionTopicRewriteOutcome[]>([]);
  const [templateFailures, setTemplateFailures] = useState<
    InspectionTopicRewriteTemplateFailure[]
  >([]);
  // Last non-null progress frame seen for this operation. The worker
  // unregisters the operation right after writing its terminal frame, so the
  // live `progress` below can go back to null before the dialog ever
  // re-renders on that terminal frame — this is what the flow falls back to
  // instead of showing "Käynnistetään..." forever.
  const [latchedProgress, setLatchedProgress] = useState<InitialisationProgress | null>(
    null,
  );

  const reset = () => {
    setStep("scope");
    setApplyToWholeProject(false);
    setApplyToBaseTemplate(false);
    setError(null);
    setOperationId(null);
    setOutcomes([]);
    setTemplateFailures([]);
    setLatchedProgress(null);
  };

  const { items } = useMyInitialisations({ enabled: open && step === "progress" });
  const activeItem = operationId
    ? items.find((item) => item.operationId === operationId)
    : undefined;
  const progress = activeItem?.progress ?? null;

  useEffect(() => {
    if (progress) setLatchedProgress(progress);
  }, [progress]);

  // Once an operation has shown progress, it can vanish from the list before
  // its terminal frame is ever observed here (see `useMyInitialisations`'s
  // header comment on the worker's unregister timing). `displayProgress`
  // renders from the latch in that case; `finishedWithoutFrame` is the rarer
  // case where no complete frame was latched either, so the run is treated
  // as finished on disappearance rather than shown as still starting up.
  const vanished = latchedProgress !== null && !progress;
  const displayProgress = progress ?? (vanished ? latchedProgress : null);
  const finishedWithoutFrame = vanished && !displayProgress?.complete;
  const progressPercent = displayProgress
    ? Math.round((displayProgress.step / displayProgress.totalSteps) * 100)
    : 0;

  /**
   * Runs the rewrite for the current scope choice. Returns `"closed"` when
   * the caller should close its dialog and refresh (a synchronous run that
   * genuinely updated something), or the step the flow moved to otherwise.
   */
  const confirm = async (
    taskId: string,
    rows: EditableTopicRow[],
  ): Promise<"closed" | InspectionTopicRewriteStep> => {
    setError(null);
    setOutcomes([]);
    setTemplateFailures([]);
    const input = {
      taskId,
      rows: rows.map(
        (row): InspectionTopicRowInput => ({
          name: row.name.trim(),
          topicId: row.topicId,
        }),
      ),
      applyToWholeProject,
      applyToBaseTemplate,
    };

    try {
      const result = await rewriteTopics(input);
      if (!result) {
        setError("Tarkastuskohtien tallennus epäonnistui. Yritä uudelleen.");
        setStep("scope");
        return "scope";
      }
      if (result.operationId) {
        setOperationId(result.operationId);
        setStep("progress");
        return "progress";
      }

      // The sync path's `success` flag is `failed == 0 && !templateFailures`,
      // which stays true when every outcome was merely skipped (e.g. the
      // inspection was already started) — so it alone cannot tell us whether
      // anything was actually written. Read the outcomes instead: report
      // "closed" only when at least one target was genuinely updated, none
      // failed, and the projektipohja rewrite (if requested) didn't fail
      // either — otherwise the failure would close unseen.
      const resultOutcomes = result.outcomes ?? [];
      const resultTemplateFailures = result.templateFailures ?? [];
      const hasUpdated = resultOutcomes.some((o) => o.status === "updated");
      const hasFailed = resultOutcomes.some((o) => o.status === "failed");

      if (hasUpdated && !hasFailed && resultTemplateFailures.length === 0) {
        return "closed";
      }

      setError(result.message);
      setOutcomes(resultOutcomes);
      setTemplateFailures(resultTemplateFailures);
      setStep("scope");
      return "scope";
    } catch (err) {
      console.error("Failed to rewrite inspection topics:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Tarkastuskohtien tallennus epäonnistui. Yritä uudelleen.",
      );
      setStep("scope");
      return "scope";
    }
  };

  return {
    step,
    setStep,
    submitting,
    applyToWholeProject,
    setApplyToWholeProject,
    applyToBaseTemplate,
    setApplyToBaseTemplate,
    error,
    outcomes,
    templateFailures,
    displayProgress,
    finishedWithoutFrame,
    progressPercent,
    confirm,
    reset,
  };
}

export type InspectionTopicRewriteFlow = ReturnType<typeof useInspectionTopicRewriteFlow>;
